import { GridApi } from '@mui/x-data-grid';
import { formatData, download } from '../../../utils/csvHelpers';
import { Logger } from '../../../utils/logger';
import type { OfficialWithSingleVerification } from '../components/TableConfigs';
import type { Verification } from '../../../networkRequest/officialsService';
import Utils from '../../../utils/utils';

const formatVerificationStatus = (verification: Verification | null): string => {
  if (!verification || verification.status === 'pending') return 'Pending';
  if (verification.status === 'verified') return 'Verified';
  if (verification.status === 'unverified') return 'Denied';
  return 'Pending';
};

const extractCellValue = (row: OfficialWithSingleVerification, field: string): string => {
  let value: unknown;

  // Handle specific fields that need special extraction logic
  switch (field) {
    case 'post_verifications':
      return formatVerificationStatus(row.post_verifications);

    case 'agency_verifications':
      return formatVerificationStatus(row.agency_verifications);

    case 'agency_name':
      return row.agency?.name || '';

    case 'agency_contact_email':
      return row.agency?.contact_email || '';

    case 'email_verified':
      return row.email_verified ? 'Verified' : 'Unverified';

    case 'name': 
      return Utils.fullName(row.first_name, row.last_name);

    default:
      // Direct field access for all other columns
      value = row[field as keyof OfficialWithSingleVerification];
      break;
  }

  // Convert to string and handle null/undefined
  if (value === null || value === undefined) return '';
  return String(value);
};

export const exportFilteredDataToCSV = (
  apiRef: React.RefObject<GridApi>,
  agencyName: string = ''
): void => {
  const api = apiRef.current;
  if (!api) {
    Logger.error('GridApi reference is null in exportFilteredDataToCSV', {});
    return;
  }

  // Get visible columns in user's preferred order
  const visibleColumns = api.getVisibleColumns();

  // Get filtered row IDs (respects search and all other filters)
  let activeRowIds = api.getAllRowIds();

  // Apply filtering if any filters are active
  const filteredRowsLookup = api.state?.filter?.filteredRowsLookup;
  if (filteredRowsLookup && Object.keys(filteredRowsLookup).length > 0) {
    activeRowIds = activeRowIds.filter((id) => filteredRowsLookup[id] !== false);
  }

  const rows = activeRowIds
    .map((id) => api.getRow(id))
    .filter(Boolean) as OfficialWithSingleVerification[];

  if (rows.length === 0) {
    Logger.warn('No rows available for CSV export in exportFilteredDataToCSV', {});
    return;
  }

  // Build CSV data respecting visible columns and their order
  const csvData = rows.map((row) => {
    const rowData: Record<string, string> = {};

    visibleColumns.forEach((column) => {
      const headerName = column.headerName || column.field;
      const cellValue = extractCellValue(row, column.field);
      rowData[headerName] = cellValue;
    });

    return rowData;
  });

  const formattedCsvData = formatData(csvData);
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `${agencyName ? `${agencyName}_` : ''}official_verifications_${timestamp}`;

  download(formattedCsvData, filename);
};
